import Base from './Base';
import moment from 'moment';
import user from '../modules/user';
import filterSegment from '../modules/filterSegment';
import User from './User';
import Tag from './Tag';
import Contact from './Contact';
import Segment from "./Segment";
import Phone from "./Phone";
import Inbox from "./Inbox";
import Phones from "./../collections/Phones";
import Inboxes from "./../collections/Inboxes";

export default class Broadcast extends Base {
    constructor(item) {
        super(item);

        if (item.user_owner) {
            this.user_owner = new User(item.user_owner);
        }

        if (item.owner) {
            this.owner = new User(item.owner);
        }

        if (item.number) {
            this.number = new Phone(item.number);
        }

        if (item.team) {
            this.team = new Inbox(item.team);
        }

        this.numbers = new Phones(item.numbers || []).models;
        this.teams = new Inboxes(item.teams || []).models;
        this.all_filters = item.all_filters || [];
        this.messages = item.messages || [];
    }

    get isStatusSent() {
        return this.status === 'sent';
    }

    get isStatusFinished() {
        return this.isStatusSent || this.isStatusStopped || this.status === 'finished';
    }

    get isStatusInProgress() {
        return this.status === 'in_progress';
    }

    get isStatusDraft() {
        return this.status === 'draft';
    }

    get isStatusScheduled() {
        return this.status === 'scheduled';
    }

    get isStatusPaused() {
        return this.status === 'pause';
    }

    get isStatusStopped() {
        return this.status === 'stopped';
    }

    get isStatusSkipped() {
        return this.status === 'skipped';
    }

    get isStatusFailed() {
        return this.status === 'failed';
    }

    get isZeroSentMessages() {
        return this.isStatusFailed && this.failed_reason === 'zero_sent_messages';
    }

    get isStatusRecurring() {
        return Boolean(this.recurring_template_settings);
    }

    get isStatusGenerateRecurring() {
        return this.isStatusRecurring && this.status !== 'recurring_template';
    }

    get isRingless() {
        return this.is_rvm;
    }

    get isStatusMainRecurring() {
        return this.isStatusRecurring && this.status === 'recurring_template';
    }

    get isViewReport() {
        return (
            this.isStatusFinished ||
            this.isStatusInProgress ||
            this.isStatusPaused
        );
    }

    get isResume() {
        return (
            this.isStatusPaused
        );
    }

    get isStop() {
        return (
            this.isStatusInProgress ||
            this.isStatusScheduled ||
            this.isStatusPaused
        );
    }

    get isTest() {
        return (
            this.isStatusDraft ||
            this.isStatusScheduled
        );
    }

    get isPause() {
        return (
            this.isStatusInProgress
        );
    }

    get isEdit() {
        return (
            this.isStatusDraft ||
            this.isStatusScheduled
        );
    }

    get isDelete() {
        return (
            this.isStatusFinished ||
            this.isStatusDraft ||
            this.isStatusScheduled ||
            this.isStatusSkipped
        );
    }

    get isFinished() {
        return this.isStatusStopped || this.isStatusSent || this.isStatusRecurringFinished;
    }

    get settingsStatus() {
        if (!this.isStatusRecurring) return '';

        return this.recurring_template_settings.status;
    }

    get isStatusRecurringFinished() {
        return this.settingsStatus === 'finished';
    }

    get isStatusRecurringActive() {
        return this.settingsStatus === 'active';
    }

    get isStatusRecurringInactive() {
        return this.settingsStatus === 'inactive';
    }

    get statusFormatted() {
        if (this.isStatusMainRecurring) {
            if (this.isStatusRecurringFinished) {
                return 'Finished';
            }

            if (this.isStatusRecurringActive) {
                return 'Active';
            }

            if (this.isStatusRecurringInactive) {
                return 'Inactive';
            }

            return this.settingsStatus;
        }

        if (this.isStatusInProgress) {
            return 'In Progress';
        }

        if (this.isStatusPaused) {
            return 'Paused';
        }

        if (this.isStatusDraft) {
            return 'Draft';
        }

        if (this.isStatusFinished) {
            return 'Finished';
        }

        if (this.isStatusFailed) {
            return 'Failed';
        }

        return this.status;
    }

    get statusClass() {
        if (this.isStatusMainRecurring) {
            if (this.isStatusRecurringFinished) {
                return 'has-background-success';
            }

            if (this.isStatusRecurringActive) {
                return 'has-background-orange';
            }

            if (this.isStatusRecurringInactive) {
                return 'has-background-grey';
            }

            if (this.isStatusFailed) {
                return 'has-background-red';
            }

            return '';
        }

        if (this.isStatusScheduled) {
            return 'has-background-blue';
        }

        if (this.isStatusFinished) {
            return 'has-background-success';
        }

        if (this.isStatusInProgress) {
            return 'has-background-orange';
        }

        if (this.isStatusDraft) {
            return 'has-background-grey';
        }

        if (this.isStatusPaused) {
            return 'has-background-dark-gray';
        }

        if (this.isStatusSkipped) {
            return 'has-background-purple';
        }

        if (this.isStatusFailed) {
            return 'has-background-red';
        }

        return '';
    }

    get canClickItem() {
        return Boolean(
            this.isStatusSent ||
            this.isStatusDraft ||
            this.isStatusScheduled ||
            this.isStatusInProgress ||
            this.isStatusPaused ||
            this.isStatusStopped
        );
    }

    get canHandelOpenView() {
        return Boolean(
            this.isStatusSent ||
            this.isStatusPaused ||
            this.isStatusInProgress ||
            this.isStatusStopped ||
            this.isStatusFailed
        );
    }

    get canHandelEdit() {
        return Boolean(
            this.isStatusDraft ||
            this.isStatusScheduled
        );
    }

    get fullNameNumberable() {
        if (this.isNumberableTypeUsersUser) {
            return this?.number?.numberable.full_name || '-';
        }

        if (this.isNumberableTypeTeamsTeam) {
            return (this?.number?.numberable.name) || 'Unnamed Inbox';
        }

        if (this.isNumberableTypeNumbersShortcode) {
            return this?.number?.numberable.name || 'Shortcode';
        }

        if (this.isNumberableSmartInbox) {
            return this.team.name || 'Unnamed Inbox';
        }

        return '';
    }

    get isNumberableTypeUsersUser() {
        if (!this.number) return false;

        return this.number.isNumberableTypeUsersUser;
    }

    get isNumberableTypeTeamsTeam() {
        if (!this.number) return false;

        return this.number.isNumberableTypeTeamsTeam;
    }

    get isNumberableTypeNumbersShortcode() {
        if (!this.number) return false;

        return this.number.isNumberableTypeNumbersShortcode;
    }

    get isNumberableSmartInbox() {
        return !this.number && this.team;
    }

    get hasShortUrls() {
        return Boolean(this.short_urls && this.short_urls.length);
    }

    get numberable() {
        return this.number.numberable;
    }

    get photoUrl() {
        if (!this.number) return '';

        return this.numberable.photo_url;
    }

    get formattedNumber() {
        if (!this.number) return '';

        return this.number.formatted_number;
    }

    get numberId() {
        if (this.number) return this.number.id;
        if (this.team) return this.team.number.id;

        return 0;
    }

    get getSmartInboxDetails() {
        if (!this.team) return '';

        return this.team.numberOrCount;
    }

    get isDefaultNumberableAvatar() {
        if (!this.numberable.photo_url) return false;

        return this.numberable.photo_url.includes(`${window.location.hostname}/images/app/default-avatar.png`);
    }

    get statisticsAll() {
        if (this.total_statistics) return [this.total_statistics];

        if (this.statistics && this.statistics.length) {
            return this.statistics;
        }

        return [];
    }

    aggregate(key) {
        if (this.statisticsAll.length) {
            return this.statisticsAll.map(stat => stat[key]).reduce((prev, next) => prev + next);
        }

        return 0;
    }

    aggregateShortLinksClicks() {
        if (this.short_urls && this.short_urls.length > 0) {
            return this.short_urls.map((url) => url.clicks).reduce((prev, next) => prev + next);
        }
        return 0;
    }

    percentage(key) {
        let total = this.aggregate('total_count');
        let contactsCount = this.contacts_count;

        if (total > 0) {
            if (key === 'short_links_clicks') {
                let percentage = (this.aggregateShortLinksClicks() / total) * 100;

                if (percentage === 100) {
                    return 100;
                }
                return percentage.toFixed();
            }

            if (key === 'delivered') {
                let percentage;

                if (contactsCount === total) {
                    percentage = 100;

                    return percentage;
                }

                percentage = (total * 100) / contactsCount;

                if (percentage === 100) {
                    return 100;
                }

                return `${percentage.toFixed()}%`;
            }

            let percentage = (this.aggregate(key) / total) * 100;

            if (percentage === 100) {
                return 100;
            }

            return percentage.toFixed();
        }

        return 0;
    }

    percentageFailed() {
        let total = this.aggregate('total_count');

        if (total > 0) {
            let failed = total - this.aggregate('success_count');
            let percentage = (failed / total) * 100;

            return percentage.toFixed();
        }

        return 0;
    }

    percentageSkipped() {
        let total = this.contacts_count;

        if (total > 0) {
            let sent = total - (this.aggregate('total_count') || 0);
            let percentage = (sent / total) * 100;

            return percentage.toFixed();
        }

        return 0;
    }

    skippedCount() {
        return this.contacts_count - (this.aggregate('total_count') || 0);
    }

    utcTime(date) {
        return user.state.user.timezone ? moment.utc(date).tz(user.state.user.timezone) : moment.utc(date).local();
    }

    get createUpdateTimeFormated() {
        if (this.updated_at) {
            return moment(this.updated_at).format('D MMM, YYYY - h:mm');
        }

        if (this.created_at) {
            return moment(this.created_at).format('D MMM, YYYY - h:mm');
        }

        return moment().format('D MMM, YYYY - h:mm');
    }

    get mediaUrl() {
        if (!this.media_url) return [];

        return this.media_url;
    }

    get color() {
        const palette = [
            '#6372A5',
            '#1D95F2',
            '#10D0AD',
            '#E13466',
            '#FFC422',
            '#FF9423',
            '#A233E1',
        ];

        return palette[this.id % palette.length];
    }

    get getSendAtFormatted() {
        return this.utcTime(this.send_at).format('MMMM Do, YYYY h:mm A');
    }

    get getSentAtFormatted() {
        return this.utcTime(this.sent_at).format('MMMM Do, YYYY h:mm A');
    }

    get textDate() {
        if (this.isStatusInProgress || this.isStatusPaused) {
            return `Sending to ${this.aggregate('total_count')}${this.sent_at ? `, beginning on ${this.getSentAtFormatted}` : ''}`;
        }

        if (this.isStatusSent) {
            return `Sent on ${this.sent_at ? this.getSentAtFormatted : this.getSendAtFormatted}`;
        }

        if (this.isStatusStopped) {
            return `Sent on ${this.sent_at ? this.getSentAtFormatted : this.getSendAtFormatted}`;
        }

        if (this.isStatusDraft || this.isStatusScheduled) {
            return `Send on ${this.send_at ? this.getSendAtFormatted : this.getSentAtFormatted}`;
        }

        return '';
    }

    get getRecurringSendAtFormatted() {
        return this.utcTime(this.send_at).format('DD/MM/yyyy [at] h:mm:A');
    }

    get getRecurringSentAtFormatted() {
        return this.utcTime(this.sent_at).format('DD/MM/yyyy [at] h:mm:A');
    }

    get textRecurringDate() {
        if (this.isStatusInProgress || this.isStatusPaused) {
            return `Scheduled ${this.aggregate('total_count')}, beginning on ${this.getRecurringSentAtFormatted}`;
        }

        if (this.isStatusSent || this.isStatusStopped) {
            return `Scheduled ${this.getRecurringSentAtFormatted}`;
        }

        if (this.isStatusDraft || this.isStatusScheduled) {
            return `Scheduled ${this.getRecurringSendAtFormatted}`;
        }

        return '';
    }

    get isAnalytics() {
        return Boolean(this.analytics);
    }

    get analytic() {
        return this.analytics || this.total_analytics;
    }

    get usersWithConversion() {
        if (!this.isAnalytics) return 0;

        return this.analytic.users_with_conversion;
    }

    get conversionRate() {
        if (!this.isAnalytics) return 0;
        if (!this.aggregate('success_count')) return 0;

        let result = (this.analytic.users_with_conversion / this.aggregate('success_count')) * 100;

        if (result === 100) {
            return 100;
        }
        return result;
    }

    get conversionValue() {
        if (!this.isAnalytics) return 0;

        return this.analytic.conversion_value;
    }

    get contactsCount() {
        let contactsCount = this.contacts_count;
        let sendedCount = this.aggregate('total_count');

        if (this.invalid_contacts_count) {
            contactsCount -= this.invalid_contacts_count;
        }

        if (contactsCount < 0) {
            contactsCount = 0;
        }

        if (this.isStatusInProgress || this.isStatusPaused) {
            if (sendedCount < contactsCount) return `${sendedCount} / ${contactsCount}`;
        }

        if (this.isStatusScheduled || this.isStatusDraft) {
            return contactsCount;
        }

        return sendedCount;
    }

    get contactsCountOrPercentage() {
        if (this.isStatusInProgress || this.isStatusPaused) {
            if (this.percentage('delivered') === 0) {
                return `0%`;
            }
            return this.percentage('delivered');
        }
        return this.contactsCount;
    }

    get numbersCheckType() {
        return this.numbers_check_type;
    }

    get filtersListGroups() {
        let filtersListGroups = {
            tags: [],
            segments: [],
            contacts: [],
            advanced: [],
        };

        if (this.filters && !this.segment_id) {
            if (Array.isArray(this.filters)) {
                filtersListGroups = {
                    tags: [],
                    segments: [],
                    contacts: [],
                    advanced: [{
                        filtersList: this.filters
                    }],
                };
            } else if (Array.isArray(this.filters.filtersList)) {
                filtersListGroups = {
                    tags: [],
                    segments: [],
                    contacts: [],
                    advanced: [{
                        filtersList: this.filters.filtersList
                    }],
                };
            } else if (this.filters.filtersListGroups) {
                filtersListGroups = this.filters.filtersListGroups;
            } else {
                filtersListGroups = {
                    tags: [],
                    segments: [],
                    contacts: [],
                    advanced: [{
                        filtersList: [
                            {
                                ...this.filters
                            }
                        ]
                    }],
                };
            }
        } else if (this.segment_id) {
            const segment = filterSegment.state.filterSegments.find(item => item.id === this.segment_id);

            filtersListGroups = {
                tags: [],
                segments: [segment || {
                    id: this.segment_id,
                    name: 'Segment deleted'
                }],
                contacts: [],
                advanced: [],
            };
        } else if (this.tags) {
            filtersListGroups = {
                tags: [],
                segments: [],
                contacts: [],
                advanced: [{
                    filtersList: [
                        {
                            filter_tags: this.tags.map((o) => o.name),
                        }
                    ]
                }],
            };
        }

        return filtersListGroups;
    }

    get filtersList() {
        let itemsAll = [];

        for (let key in this.filtersListGroups) {
            const items = this.filtersListGroups[key];

            if (key === 'tags') {
                itemsAll.push(
                    ...items.map(item => new Tag(item).filters)
                );
            }

            if (key === 'contacts') {
                itemsAll.push(
                    ...items.map(item => new Contact(item).filters)
                );
            }

            if (key === 'segments') {
                const segments = filterSegment.state.filterSegments;

                itemsAll.push(
                    ...items.map(item => (segments.find(s => s.id === item.id) || new Segment(item)).filters)
                );
            }

            if (key === 'advanced') {
                itemsAll.push(
                    ...items.map(item => item)
                );
            }
        }

        return itemsAll.reduce((c, n) => {
            return [...c, ...n.filtersList];
        }, []);
    }

    handleFiltersListRecipients({ filters: filtersType, operators: filtersOperator }) {
        let filtersList = this.filtersList;
        let filtersListRecipients = filtersList.map(filters => {
            if (!filters.filters) filters.filters = [];
            if (!filters.custom_filters) filters.custom_filters = [];
            if (!filters.filter_tags) filters.filter_tags = [];

            filters = [
                ...filters.filters,
                ...filters.custom_filters,
                ...filters.filter_tags.map(item => ({ value: item, }))
            ];

            filters = filters.map(item => {
                const filterType = filtersType.find(filter => filter.key === item.key);

                if (!filterType) {
                    return {
                        ...item,
                        labelType: 'Tag',
                        labelOperator: 'equals',
                    };
                }

                const filterOperator = filtersOperator[filterType.type].find(operator => operator.key === item.operator);

                return {
                    ...item,
                    labelType: filterType.name,
                    labelOperator: filterOperator.name,
                };
            });

            filters = filters.map(item => {
                if (!item.labelOperator) {
                    if (filtersList.length > 1) {
                        return `${item.labelType} ${item.value}`;
                    }

                    return `[ ${item.labelType} ${item.value} ]`;
                }

                if (filtersList.length > 1) {
                    return `${item.labelType} ${item.labelOperator} ${item.value}`;
                }

                return `[ ${item.labelType} ${item.labelOperator} ${item.value} ]`;
            });

            return filters.join(' AND ');
        });

        filtersListRecipients = filtersList.length > 1
            ? filtersListRecipients.map(item => `[ ${item} ]`).join(' OR ')
            : filtersListRecipients.join(' OR ');

        return filtersListRecipients;
    }

    get filtersListGroupsArray() {
        return this.all_filters.map(filters => {
            let filtersListGroups = {
                tags: [],
                segments: [],
                contacts: [],
                advanced: [],
            };

            if (filters) {
                if (Array.isArray(filters)) {
                    filtersListGroups = {
                        tags: [],
                        segments: [],
                        contacts: [],
                        advanced: [{
                            filtersList: filters
                        }],
                    };
                } else if (Array.isArray(filters.filtersList)) {
                    filtersListGroups = {
                        tags: [],
                        segments: [],
                        contacts: [],
                        advanced: [{
                            filtersList: filters.filtersList
                        }],
                    };
                } else if (filters.filtersListGroups) {
                    filtersListGroups = filters.filtersListGroups;
                } else {
                    filtersListGroups = {
                        tags: [],
                        segments: [],
                        contacts: [],
                        advanced: [{
                            filtersList: [
                                {
                                    ...filters
                                }
                            ]
                        }],
                    };
                }
            }

            return filtersListGroups;
        });
    }

    get filtersListArray() {
        return this.filtersListGroupsArray.map(filtersListGroups => {
            let itemsAll = [];

            for (let key in filtersListGroups) {
                const items = filtersListGroups[key];

                if (key === 'contacts') {
                    itemsAll.push(
                        ...items.map(item => new Contact(item).filters)
                    );
                }

                if (key === 'advanced') {
                    itemsAll.push(
                        ...items.map(item => item)
                    );
                }
            }

            return itemsAll.reduce((c, n) => {
                return [...c, ...n.filtersList];
            }, []);
        });
    }

    handleFiltersListRecipientsArray({ filters: filtersType, operators: filtersOperator }) {
        return this.filtersListArray.map(filtersList => {
            let filtersListRecipients = filtersList.map(filters => {
                if (!filters.filters) filters.filters = [];
                if (!filters.custom_filters) filters.custom_filters = [];
                if (!filters.filter_tags) filters.filter_tags = [];

                filters = [
                    ...filters.filters,
                    ...filters.custom_filters,
                    ...filters.filter_tags.map(item => ({ value: item, }))
                ];

                filters = filters.map(item => {
                    const filterType = filtersType.find(filter => filter.key === item.key);

                    if (!filterType) {
                        return {
                            ...item,
                            labelType: 'Tag',
                            labelOperator: 'equals',
                        };
                    }

                    const filterOperator = filtersOperator[filterType.type].find(operator => operator.key === item.operator);

                    return {
                        ...item,
                        labelType: filterType.name,
                        labelOperator: filterOperator.name,
                    };
                });

                filters = filters.map(item => {
                    if (!item.labelOperator) {
                        if (filtersList.length > 1) {
                            return `${item.labelType} ${item.value}`;
                        }

                        return `[ ${item.labelType} ${item.value} ]`;
                    }

                    if (filtersList.length > 1) {
                        return `${item.labelType} ${item.labelOperator} ${item.value}`;
                    }

                    return `[ ${item.labelType} ${item.labelOperator} ${item.value} ]`;
                });

                return filters.join(' AND ');
            });

            filtersListRecipients = filtersList.length > 1
                ? filtersListRecipients.map(item => `[ ${item} ]`).join(' OR ')
                : filtersListRecipients.join(' OR ');

            return filtersListRecipients;
        });
    }
}
